import React from 'react';
import { Button, Tooltip } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';

interface PrintButtonProps {
  targetRef: React.RefObject<HTMLElement>;
  tooltip?: string;
  style?: React.CSSProperties;
  printTitle?: string;
  /** Clone the element itself (outerHTML) instead of only its children. */
  cloneRoot?: boolean;
  extraCss?: string;
}

const PrintButton: React.FC<PrintButtonProps> = ({
  targetRef,
  tooltip = 'Print',
  style,
  printTitle,
  cloneRoot = false,
  extraCss = '',
}) => {
  const handlePrint = () => {
    if (!targetRef.current) return;
    // Sadece tabloyu (pagination ve gereksiz alanlar olmadan) yazdırmak için özel css ekle
    const printContents = cloneRoot
      ? targetRef.current.outerHTML
      : targetRef.current.innerHTML;
    const printWindow = window.open('', '', 'height=600,width=900');
    if (!printWindow) return;
    printWindow.document.write('<html><head>');
    // Tarayıcı üst bilgi başlığını boş bırak
    printWindow.document.write('<title> </title>');
    Array.from(document.styleSheets).forEach((sheet) => {
      try {
        if (sheet.href) {
          printWindow.document.write(`<link rel="stylesheet" href="${sheet.href}">`);
        }
      } catch {}
    });
    // Ant Design v5 (css-in-js) ve runtime eklenen stiller için:
    // href'siz <style> tag'lerini de yazdırma penceresine kopyala.
    Array.from(document.querySelectorAll('style')).forEach((styleEl) => {
      try {
        printWindow.document.write(styleEl.outerHTML);
      } catch {}
    });
    printWindow.document.write(`
      <style>
        @page { size: A4; margin: 12mm; }
        html, body { background: #fff !important; }
        body { margin: 0 !important; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'; font-size: 12px; color: #000; }

        /* Tablo düzeni (SADECE Zimmet Formu tablosu için) */
        .equipment-table.ant-table-wrapper,
        .equipment-table .ant-table,
        .equipment-table .ant-table-bordered { width: 100% !important; }

        /* fixed layout A4'te yatay kaydırma yapabiliyor; auto + wrap daha güvenli */
        .equipment-table .ant-table table { width: 100% !important; table-layout: auto !important; }

        .equipment-table .ant-table td,
        .equipment-table .ant-table th { padding: 6px 8px !important; }

        .equipment-table .ant-table-tbody > tr > td { border-bottom: 1px solid #000 !important; }
        .equipment-table { font-size: 13px; }
        .equipment-table .ant-table-thead > tr > th { font-size: 13px; font-weight: 600; }
        .equipment-table .ant-table-tbody > tr > td { font-size: 13px; }
        .equipment-table .eq-cell { font-size: 13px !important; }

        .equipment-table .ant-table-cell { white-space: normal !important; word-break: break-word !important; }
        .equipment-table.ant-table-wrapper,
        .equipment-table .ant-table-container,
        .equipment-table .ant-table-content,
        .equipment-table .ant-table-body { overflow: visible !important; }

        /* Başlık alanı */
        .print-header { text-align: center; margin: 0 0 12px 0; }
        .print-header h2 { margin: 0; font-size: 18px; font-weight: 600; }

        /* Yazdırmada görünmeyecekler */
        .ant-table-pagination,
        .ant-pagination,
        .ant-table-footer,
        .ant-table-thead > tr > th:last-child,
        .ant-table-tbody > tr > td:last-child,
        .no-print { display: none !important; }

        /* Ekipman tablosunda son sütunu (Atama Tarihi) gizleme! */
        .equipment-table .ant-table-thead > tr > th:last-child,
        .equipment-table .ant-table-tbody > tr > td:last-child { display: table-cell !important; }

        /* A4 yerleşim artırımı */
        .a4-container { max-width: 186mm; margin: 0 auto; }
        .a4-section { break-inside: avoid; page-break-inside: avoid; }
        .signature-card { min-height: 45mm; display: flex; flex-direction: column; }
        .signature-line { height: 56px; border-top: 1px solid #000; }

        /* İmza satırı yan yana kalsın */
        .signature-row { display: flex !important; flex-wrap: nowrap !important; }
        .signature-row .ant-col { flex: 0 0 50% !important; max-width: 50% !important; padding-left: 0 !important; padding-right: 0 !important; }
        .signature-row .ant-row { margin-left: 0 !important; margin-right: 0 !important; }

        /* Genel grid gutterlarını yazdırmada sıfırla */
        .ant-row { margin-left: 0 !important; margin-right: 0 !important; }
        .ant-col { padding-left: 0 !important; padding-right: 0 !important; }

        @media print {
          .no-print { display: none !important; }
        }
        ${extraCss}
      </style>
    `);
    printWindow.document.write('</head><body>');
    if (printTitle) {
      printWindow.document.write(`<div class="print-header"><h2>${printTitle}</h2></div>`);
    }
    printWindow.document.write(printContents);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      try { printWindow.document.title = ' '; } catch {}
      printWindow.print();
      printWindow.close();
    }, 400);
  };

  return (
    <Tooltip title={tooltip}>
      <Button icon={<PrinterOutlined />} onClick={handlePrint} style={style} />
    </Tooltip>
  );
};

export default PrintButton;
